import React, { useCallback, useEffect, useState } from 'react';
import { adminDashboard, adminCobrancaDireta } from '../../lib/api';

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dm = (d) => d.slice(8, 10) + '/' + d.slice(5, 7);

const Card = ({ titulo, valor, sub, destaque }) => (
  <div className={`rounded-2xl border p-4 ${destaque ? 'bg-gradient-to-br from-[#1E429F] to-[#2A5BC7] text-white border-transparent' : 'bg-white border-slate-100'}`}>
    <p className={`text-xs font-medium ${destaque ? 'text-white/80' : 'text-slate-500'}`}>{titulo}</p>
    <p className={`text-2xl font-extrabold mt-1 ${destaque ? 'text-white' : 'text-slate-900'}`}>{valor}</p>
    {sub && <p className={`text-[11px] mt-1 ${destaque ? 'text-white/70' : 'text-slate-500'}`}>{sub}</p>}
  </div>
);

const Barras = ({ titulo, dados, campo, formato, cor, campo2, cor2, legenda }) => {
  const max = Math.max(1, ...dados.map((d) => Math.max(Number(d[campo]) || 0, campo2 ? Number(d[campo2]) || 0 : 0)));
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-slate-900">{titulo}</p>
        {legenda && <div className="flex gap-3 text-[11px] text-slate-500">{legenda}</div>}
      </div>
      <div className="flex items-end gap-[3px] h-32">
        {dados.map((d) => (
          <div key={d.dia} className="flex-1 flex items-end justify-center gap-[1px] h-full" title={`${dm(d.dia)}: ${formato(d[campo])}${campo2 ? ` · ${d[campo2]}` : ''}`}>
            <div className={`${cor} rounded-t w-full`} style={{ height: `${Math.max(((Number(d[campo]) || 0) / max) * 100, d[campo] ? 3 : 0)}%` }} />
            {campo2 && <div className={`${cor2} rounded-t w-full`} style={{ height: `${Math.max(((Number(d[campo2]) || 0) / max) * 100, d[campo2] ? 3 : 0)}%` }} />}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>{dm(dados[0].dia)}</span><span>{dm(dados[dados.length - 1].dia)}</span></div>
    </div>
  );
};

const mesAtual = () => new Date().toISOString().slice(0, 7);

const DashboardSection = () => {
  const [dias, setDias] = useState(30);
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);
  const [mes, setMes] = useState(mesAtual());
  const [cd, setCd] = useState(null);

  const carregar = useCallback(() => {
    setErro(null);
    adminDashboard(dias).then(setD).catch(() => setErro('Não foi possível carregar o painel.'));
  }, [dias]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { adminCobrancaDireta(mes).then(setCd).catch(() => setCd(null)); }, [mes]);

  const csv = () => {
    if (!cd) return;
    const linhas = [['Usuário', 'E-mail', 'Envios', 'Custo no contrato (R$)', 'Valor de venda (R$)'],
      ...cd.usuarios.flatMap((u) => [
        [u.nome, u.email || '', u.envios, u.custo.toFixed(2).replace('.', ','), u.valor_venda.toFixed(2).replace('.', ',')],
        ...(u.funcionarios || []).map((f) => [`   emitido por: ${f.nome}`, '', f.envios, f.custo.toFixed(2).replace('.', ','), '']),
      ]),
      ['TOTAL', '', cd.total.envios, cd.total.custo.toFixed(2).replace('.', ','), cd.total.valor_venda.toFixed(2).replace('.', ',')]];
    const texto = '﻿' + linhas.map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([texto], { type: 'text/csv;charset=utf-8' }));
    a.download = `cobranca-direta-${cd.mes}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (erro) return <p className="text-sm text-red-600">{erro}</p>;
  if (!d) return <p className="text-sm text-slate-500">Carregando painel...</p>;
  const { lucro, envios, dinheiro, acessos, serie } = d;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-600 mr-1">Período:</span>
        {[7, 30, 90].map((n) => (
          <button key={n} type="button" onClick={() => setDias(n)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${dias === n ? 'bg-[#2A5BC7] text-white border-[#2A5BC7]' : 'border-slate-200 text-slate-600 hover:border-[#2A5BC7]'}`}>Últimos {n} dias</button>
        ))}
      </div>

      <section>
        <h3 className="text-sm font-bold text-slate-900 mb-2">Lucro</h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card titulo="Vendas de frete" valor={brl(lucro.receita)} sub={`${envios.carteira} envios pagos na carteira`} />
          <Card titulo="Custo nos Correios" valor={brl(lucro.custo_contrato)} sub="do seu contrato" />
          <Card titulo="Lucro bruto" valor={brl(lucro.lucro_bruto)} sub="vendas − custo" />
          <Card titulo="Bônus concedidos" valor={brl(lucro.bonus_concedidos)} sub="primeiro envio" />
          <Card destaque titulo="Lucro líquido" valor={brl(lucro.lucro_liquido)} sub="depois dos bônus" />
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Não inclui as taxas do InfinitePay sobre as recargas. Envios cancelados não entram.</p>
      </section>

      <section>
        <h3 className="text-sm font-bold text-slate-900 mb-2">Dinheiro e envios</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card titulo="Recargas pagas" valor={brl(dinheiro.recargas_pagas)} sub={`${dinheiro.recargas_qtd} pagamento(s)`} />
          <Card titulo="Saldo nas carteiras" valor={brl(dinheiro.saldo_nas_carteiras)} sub="dinheiro de clientes ainda não usado" />
          <Card titulo="Cobrança direta" valor={brl(lucro.custo_cobranca_direta)} sub={`${envios.direta} envio(s), custo que fica com você`} />
          <Card titulo="Cancelados" valor={envios.cancelados} sub="no período" />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-bold text-slate-900 mb-2">Acessos</h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card titulo="Visitantes hoje" valor={acessos.visitantes_hoje} sub={`${acessos.visitantes_periodo} no período`} />
          <Card titulo="Logins em 24 h" valor={acessos.logins_24h} sub={`${acessos.logins} no período`} />
          <Card titulo="Usuários ativos" valor={acessos.usuarios_ativos} sub="entraram no período" />
          <Card titulo="Novos usuários" valor={acessos.novos_usuarios} sub="cadastros no período" />
          <Card titulo="Total de usuários" valor={acessos.usuarios_total} />
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-4">
        <Barras titulo="Lucro bruto por dia" dados={serie} campo="lucro" formato={brl} cor="bg-emerald-500" />
        <Barras titulo="Acessos por dia" dados={serie} campo="visitantes" formato={(v) => `${v} visitantes`} cor="bg-[#2A5BC7]" campo2="logins" cor2="bg-[#F77820]"
          legenda={<><span className="flex items-center gap-1"><i className="w-2 h-2 rounded-sm bg-[#2A5BC7] inline-block" />visitantes</span><span className="flex items-center gap-1"><i className="w-2 h-2 rounded-sm bg-[#F77820] inline-block" />logins</span></>} />
      </div>

      <section className="bg-white rounded-2xl border border-slate-100 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Cobrança direta por usuário</h3>
            <p className="text-xs text-slate-500">Envios sem débito na carteira: o custo fica no seu contrato dos Correios.</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="month" value={mes} onChange={(e) => setMes(e.target.value || mesAtual())} className="h-9 rounded-md border border-slate-200 px-2 text-sm" />
            <button type="button" onClick={csv} disabled={!cd || cd.usuarios.length === 0} className="h-9 px-3 rounded-full border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">Baixar CSV</button>
          </div>
        </div>
        {cd && cd.usuarios.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-100"><tr><th className="py-2">Usuário</th><th className="py-2 text-right">Envios</th><th className="py-2 text-right">Custo no contrato</th><th className="py-2 text-right">Valor de venda</th></tr></thead>
              <tbody>
                {cd.usuarios.map((u) => (
                  <tr key={u.user_id} className="border-b border-slate-50"><td className="py-2">{u.nome}<span className="block text-xs text-slate-500">{u.email}</span>
                    {(u.funcionarios || []).map((f) => <span key={f.nome} className="block text-xs text-slate-600 pl-3">↳ {f.nome}: {f.envios} envio(s), {brl(f.custo)}</span>)}</td><td className="py-2 text-right">{u.envios}</td><td className="py-2 text-right font-medium">{brl(u.custo)}</td><td className="py-2 text-right text-slate-500">{brl(u.valor_venda)}</td></tr>
                ))}
                <tr className="font-bold"><td className="py-2">Total</td><td className="py-2 text-right">{cd.total.envios}</td><td className="py-2 text-right">{brl(cd.total.custo)}</td><td className="py-2 text-right">{brl(cd.total.valor_venda)}</td></tr>
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-slate-500">Nenhum envio com cobrança direta neste mês.</p>}
      </section>
    </div>
  );
};

export default DashboardSection;
